import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronUp, ChevronDown, ChevronsUpDown, Search, Filter, X, User,
  Calendar, Clock, Tag, AlertTriangle, CheckSquare, Square, Trash2, Archive, UserCheck
} from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Checkbox } from './ui/checkbox';
import axios from 'axios';
import { getApiUrl } from '../utils/config';
import { toast } from 'react-hot-toast';

const PRIORITY_META = {
  1: { label: 'Low', cls: 'bg-slate-100 text-slate-600' },
  2: { label: 'Medium', cls: 'bg-amber-100 text-amber-700' },
  3: { label: 'High', cls: 'bg-rose-100 text-rose-700' },
};

const STATUS_META = {
  todo:        { label: 'To Do',       cls: 'bg-slate-100 text-slate-600'    },
  in_progress: { label: 'In Progress', cls: 'bg-amber-100 text-amber-700'    },
  in_review:   { label: 'In Review',   cls: 'bg-violet-100 text-violet-700'  },
  approved:    { label: 'Approved',    cls: 'bg-emerald-100 text-emerald-700' },
};

const fmt = d => d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '—';
const initials = v => (v || 'U').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();

const SortIcon = ({ col, sort }) => {
  if (sort.col !== col) return <ChevronsUpDown className="w-3 h-3 text-slate-300" />;
  return sort.dir === 'asc' ? <ChevronUp className="w-3 h-3 text-[#5F2AA8]" /> : <ChevronDown className="w-3 h-3 text-[#5F2AA8]" />;
};

const TaskListView = ({
  tasks = [],
  members = [],
  sprints = [],
  orgId,
  onTaskClick,
  onBulkAction,
  canManage,
  currentUserId,
}) => {
  const [search, setSearch] = useState('');
  const [priFilter, setPriFilter] = useState('all');
  const [asnFilter, setAsnFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [sprintFilter, setSprintFilter] = useState('all');
  const [sort, setSort] = useState({ col: 'position', dir: 'asc' });
  const [selected, setSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkAssignee, setBulkAssignee] = useState('');
  const [bulkStatus, setBulkStatus] = useState('');

  // All unique tags across tasks
  const allTags = useMemo(() => {
    const s = new Set();
    tasks.forEach(t => (t.tags || []).forEach(g => s.add(g)));
    return [...s].sort();
  }, [tasks]);

  const filtered = useMemo(() => {
    let list = tasks.filter(t => !t.archived);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(t => `${t.title} ${t.description || ''}`.toLowerCase().includes(q));
    }
    if (priFilter !== 'all') list = list.filter(t => String(t.priority) === priFilter);
    if (asnFilter !== 'all') list = list.filter(t => asnFilter === 'unassigned' ? !t.assignee_id : t.assignee_id === asnFilter);
    if (statusFilter !== 'all') list = list.filter(t => t.status === statusFilter);
    if (tagFilter !== 'all') list = list.filter(t => (t.tags || []).includes(tagFilter));
    if (sprintFilter !== 'all') list = list.filter(t => sprintFilter === 'backlog' ? !t.sprint_id : t.sprint_id === sprintFilter);

    // Sort
    list = [...list].sort((a, b) => {
      let av = a[sort.col], bv = b[sort.col];
      if (sort.col === 'due_date') { av = av ? new Date(av) : null; bv = bv ? new Date(bv) : null; }
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return sort.dir === 'asc' ? -1 : 1;
      if (av > bv) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [tasks, search, priFilter, asnFilter, statusFilter, tagFilter, sprintFilter, sort]);

  const toggleSort = col => setSort(s => s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
  const hasFilters = search || priFilter !== 'all' || asnFilter !== 'all' || statusFilter !== 'all' || tagFilter !== 'all' || sprintFilter !== 'all';
  const clearFilters = () => { setSearch(''); setPriFilter('all'); setAsnFilter('all'); setStatusFilter('all'); setTagFilter('all'); setSprintFilter('all'); };

  const toggleSelect = id => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(s => s.size === filtered.length ? new Set() : new Set(filtered.map(t => t.id)));

  const execBulk = async (action, payload = {}) => {
    if (selected.size === 0) return;
    setBulkLoading(true);
    try {
      await onBulkAction([...selected], action, payload);
      setSelected(new Set());
      toast.success(`${action} applied to ${selected.size} task(s)`);
    } catch { toast.error('Bulk action failed'); } finally { setBulkLoading(false); }
  };

  const COL_HDR = 'flex items-center gap-1 cursor-pointer select-none hover:text-foreground transition-colors';

  return (
    <div className="space-y-3">
      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center rounded-2xl border border-slate-200/70 bg-white/75 p-3 shadow-sm backdrop-blur">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tasks…" className="pl-9 h-8" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[130px] h-8 text-xs">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="todo">To Do</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="in_review">In Review</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
          </SelectContent>
        </Select>
        <Select value={priFilter} onValueChange={setPriFilter}>
          <SelectTrigger className="w-[120px] h-8 text-xs">
            <Filter className="mr-1 h-3 w-3 text-muted-foreground" /><SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            <SelectItem value="1">Low</SelectItem>
            <SelectItem value="2">Medium</SelectItem>
            <SelectItem value="3">High</SelectItem>
          </SelectContent>
        </Select>
        <Select value={asnFilter} onValueChange={setAsnFilter}>
          <SelectTrigger className="w-[140px] h-8 text-xs">
            <User className="mr-1 h-3 w-3 text-muted-foreground" /><SelectValue placeholder="Assignee" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All assignees</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {members.map(m => <SelectItem key={m.user_id || m.id} value={m.user_id || m.id}>{m.full_name || m.email}</SelectItem>)}
          </SelectContent>
        </Select>
        {allTags.length > 0 && (
          <Select value={tagFilter} onValueChange={setTagFilter}>
            <SelectTrigger className="w-[120px] h-8 text-xs">
              <Tag className="mr-1 h-3 w-3 text-muted-foreground" /><SelectValue placeholder="Tag" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tags</SelectItem>
              {allTags.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {sprints.length > 0 && (
          <Select value={sprintFilter} onValueChange={setSprintFilter}>
            <SelectTrigger className="w-[140px] h-8 text-xs">
              <SelectValue placeholder="Sprint" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sprints</SelectItem>
              <SelectItem value="backlog">Backlog</SelectItem>
              {sprints.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {hasFilters && (
          <Button type="button" variant="ghost" size="sm" onClick={clearFilters} className="h-8">
            <X className="h-3.5 w-3.5 mr-1" /> Clear
          </Button>
        )}
        <span className="ml-auto text-xs text-muted-foreground whitespace-nowrap">{filtered.length} task{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Bulk action bar */}
      <AnimatePresence>
        {selected.size > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            className="flex flex-wrap items-center gap-2 rounded-xl border border-[#d4b8f0] bg-[#f3eeff] px-4 py-2.5"
          >
            <span className="text-sm font-medium text-[#4B1E87]">{selected.size} selected</span>
            <div className="flex flex-wrap gap-2 ml-2">
              <Select value={bulkStatus} onValueChange={v => { setBulkStatus(v); execBulk('update', { status: v }); }}>
                <SelectTrigger className="h-7 text-xs w-[130px] bg-white border-[#d4b8f0]">
                  <SelectValue placeholder="Change status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todo">→ To Do</SelectItem>
                  <SelectItem value="in_progress">→ In Progress</SelectItem>
                  <SelectItem value="in_review">→ In Review</SelectItem>
                  <SelectItem value="approved">→ Approved</SelectItem>
                </SelectContent>
              </Select>
              <Select value={bulkAssignee} onValueChange={v => { setBulkAssignee(v); execBulk('update', { assignee_id: v === 'unassigned' ? null : v }); }}>
                <SelectTrigger className="h-7 text-xs w-[140px] bg-white border-[#d4b8f0]">
                  <UserCheck className="mr-1 w-3 h-3" /><SelectValue placeholder="Assign to…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassign</SelectItem>
                  {members.map(m => <SelectItem key={m.user_id || m.id} value={m.user_id || m.id}>{m.full_name || m.email}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={() => execBulk('archive')} disabled={bulkLoading} className="h-7 text-xs border-[#d4b8f0] bg-white">
                <Archive className="w-3 h-3 mr-1" /> Archive
              </Button>
              {canManage && (
                <Button size="sm" variant="outline" onClick={() => execBulk('delete')} disabled={bulkLoading} className="h-7 text-xs border-rose-200 bg-white text-rose-600 hover:bg-rose-50">
                  <Trash2 className="w-3 h-3 mr-1" /> Delete
                </Button>
              )}
            </div>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="h-7 text-xs ml-auto">
              <X className="w-3 h-3" />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Table */}
      <div className="rounded-2xl border border-slate-200/70 bg-white shadow-sm overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-[32px_1fr_100px_90px_120px_90px_80px] gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-100 text-xs font-medium text-muted-foreground">
          <div className="flex items-center">
            <button onClick={toggleAll}>
              {selected.size > 0 && selected.size === filtered.length
                ? <CheckSquare className="w-4 h-4 text-[#5F2AA8]" />
                : <Square className="w-4 h-4 text-slate-300" />}
            </button>
          </div>
          <button className={COL_HDR} onClick={() => toggleSort('title')}>Title <SortIcon col="title" sort={sort} /></button>
          <button className={COL_HDR} onClick={() => toggleSort('status')}>Status <SortIcon col="status" sort={sort} /></button>
          <button className={COL_HDR} onClick={() => toggleSort('priority')}>Priority <SortIcon col="priority" sort={sort} /></button>
          <div className="flex items-center gap-1"><User className="w-3 h-3" />Assignee</div>
          <button className={COL_HDR} onClick={() => toggleSort('due_date')}><Calendar className="w-3 h-3" />Due <SortIcon col="due_date" sort={sort} /></button>
          <button className={COL_HDR} onClick={() => toggleSort('estimate_hrs')}><Clock className="w-3 h-3" />Est <SortIcon col="estimate_hrs" sort={sort} /></button>
        </div>

        {/* Rows */}
        {filtered.length === 0 ? (
          <div className="py-16 text-center text-sm text-muted-foreground">No tasks match your filters</div>
        ) : (
          <AnimatePresence initial={false}>
            {filtered.map((task, i) => {
              const assignee = members.find(m => (m.user_id || m.id) === task.assignee_id);
              const priority = PRIORITY_META[task.priority] || PRIORITY_META[2];
              const statusMeta = STATUS_META[task.status] || STATUS_META.todo;
              const isOverdue = task.due_date && task.status !== 'approved' && new Date(task.due_date) < new Date();
              const isSelected = selected.has(task.id);

              return (
                <motion.div
                  key={task.id}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className={`grid grid-cols-[32px_1fr_100px_90px_120px_90px_80px] gap-2 px-4 py-3 border-b border-slate-50 items-center
                    hover:bg-slate-50/60 transition-colors cursor-pointer
                    ${isSelected ? 'bg-[#f3eeff]/60' : ''}`}
                >
                  <div onClick={e => { e.stopPropagation(); toggleSelect(task.id); }} className="flex items-center">
                    {isSelected
                      ? <CheckSquare className="w-4 h-4 text-[#5F2AA8]" />
                      : <Square className="w-4 h-4 text-slate-300 hover:text-slate-500" />}
                  </div>
                  <div onClick={() => onTaskClick?.(task)} className="min-w-0">
                    <p className="text-sm font-medium truncate hover:text-[#4B1E87] transition-colors">{task.title}</p>
                    {(task.tags || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {task.tags.slice(0, 3).map(t => (
                          <span key={t} className="text-[10px] px-1.5 py-0 rounded-full bg-[#f3eeff] text-[#4B1E87] border border-indigo-100">{t}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div onClick={() => onTaskClick?.(task)}>
                    <Badge className={`text-[10px] px-1.5 py-0 ${statusMeta.cls}`}>{statusMeta.label}</Badge>
                  </div>
                  <div onClick={() => onTaskClick?.(task)}>
                    <Badge className={`text-[10px] px-1.5 py-0 ${priority.cls}`}>{priority.label}</Badge>
                  </div>
                  <div onClick={() => onTaskClick?.(task)} className="flex items-center gap-1.5 min-w-0">
                    {assignee ? (
                      <>
                        <Avatar className="w-5 h-5 shrink-0">
                          <AvatarFallback className="text-[9px] bg-[#ede0ff] text-[#4B1E87]">{initials(assignee.full_name)}</AvatarFallback>
                        </Avatar>
                        <span className="text-xs truncate text-muted-foreground">{assignee.full_name?.split(' ')[0] || '—'}</span>
                      </>
                    ) : (
                      <span className="text-xs text-muted-foreground/50">—</span>
                    )}
                  </div>
                  <div onClick={() => onTaskClick?.(task)}>
                    {task.due_date ? (
                      <span className={`text-xs font-medium flex items-center gap-1 ${isOverdue ? 'text-rose-600' : 'text-muted-foreground'}`}>
                        {isOverdue && <AlertTriangle className="w-3 h-3" />}
                        {fmt(task.due_date)}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground/40">—</span>
                    )}
                  </div>
                  <div onClick={() => onTaskClick?.(task)}>
                    {task.estimate_hrs ? (
                      <span className="text-xs text-muted-foreground">{task.estimate_hrs}h</span>
                    ) : (
                      <span className="text-xs text-muted-foreground/40">—</span>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default TaskListView;
